import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ChasseHuntMap, { createDivIcon } from '../components/ChasseHuntMap';
import PageShell from '../components/PageShell';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import useEventSource from '../hooks/useEventSource';

// ── Design system ─────────────────────────────────────────────────────────────

const VERT = 'var(--couleur-bordure-forte)';
const OR = 'var(--couleur-or)';
const FOND = 'var(--couleur-fond)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.14)';

const PAS_MISE = 10; // Incrément minimum de mise (CAD)
// Polling fallback (très lent) — SSE est le canal primaire. Les 3 minutes
// servent uniquement à rattraper un drift dans les rares cas où SSE
// échouerait silencieusement (firewall corporate qui buffere, proxy
// agressif). En conditions normales, l'event bus délivre les bids en
// < 1 s et ce poll ne ramène jamais de delta.
const POLLING_MS = 180_000;

// ── Référentiels (libellés bilingues) ─────────────────────────────────────────

const ESPECES = {
  orignal: { fr: 'Orignal', en: 'Moose', icon: '🫎' },
  cerf_virginie: { fr: 'Cerf de Virginie', en: 'White-tailed deer', icon: '🦌' },
  ours_noir: { fr: 'Ours noir', en: 'Black bear', icon: '🐻' },
  dindon_sauvage: { fr: 'Dindon sauvage', en: 'Wild turkey', icon: '🦃' },
  petit_gibier: { fr: 'Petit gibier', en: 'Small game', icon: '🐇' },
  oiseaux_migrateurs: { fr: 'Oiseaux migrateurs', en: 'Migratory birds', icon: '🦆' },
  coyote: { fr: 'Coyote', en: 'Coyote', icon: '🐺' },
  marmotte: { fr: 'Marmotte', en: 'Marmot', icon: '🐿' },
  autre: { fr: 'Autre', en: 'Other', icon: '🐾' },
};

const ARMES = {
  arme_a_feu: { fr: 'Arme à feu', en: 'Firearm', icon: '🔫' },
  arc: { fr: 'Arc', en: 'Bow', icon: '🏹' },
  arbalete: { fr: 'Arbalète', en: 'Crossbow', icon: '🎯' },
  poudre_noire: { fr: 'Poudre noire', en: 'Black powder', icon: '💥' },
  air_comprime: { fr: 'Air comprimé', en: 'Compressed air', icon: '💨' },
};

// ── Traductions ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    chargement: 'Chargement…',
    erreur: 'Impossible de charger cet encan.',
    introuvable: 'Encan introuvable.',
    retour: '← Retour aux terres',
    photoSeule: 'Aucune photo available',

    // Section infos chasse
    sectionChasse: 'Informations de chasse',
    especesAutorisees: 'Espèces autorisées',
    armesAutorisees: 'Armes autorisées',
    saisonChasse: 'Saison de chasse',
    superficieCh: 'Superficie chassable',
    zone: 'Zone de chasse',
    capacite: 'Capacité',
    placesDispo: (d, t) => `${d} place${d > 1 ? 's' : ''} available${d > 1 ? 's' : ''} sur ${t}`,
    reglements: 'Règlements applicables',
    voirReglements: 'Voir les règlements',
    masquerReglements: 'Masquer les règlements',
    aucunReglement: 'Aucun règlement available.',
    hebergement: 'Hébergement inclus',
    guide: 'Guide inclus',
    notes: 'Notes du propriétaire',

    // Encan
    sectionEncan: 'Encan en cours',
    prixActuel: 'Prix actuel',
    prixPlancher: 'Prix plancher',
    aucuneMise: "Aucune mise pour l'instant",
    historiqueMises: 'Historique des dernières bids',
    placerMise: 'Placer une mise',
    miseMin: (m) => `Mise minimum : ${m.toFixed(2)} $`,
    montantInvalide: `Le amount doit être supérieur d'au moins ${PAS_MISE}$ à la mise actuelle.`,
    placer: 'Placer ma mise',
    placement: 'Mise en cours…',
    miseSucces: 'Mise placée avec succès !',
    connexionRequise: 'Connectez-vous pour placer une mise.',
    chasseurUniquement: 'Réservé aux chasseurs.',

    // Compte à rebours
    fermetureDans: 'Ferme dans',
    encanTermine: 'Encan terminé',
    jours: 'j',
    heures: 'h',
    minutes: 'm',
    secondes: 's',

    // Prix fixe
    sectionPrixFixe: 'Réservation à prix fixe',
    prixParChasseur: 'Prix par chasseur',
    placesRestantes: (r, t) => `${r} place${r > 1 ? 's' : ''} restante${r > 1 ? 's' : ''} sur ${t}`,
    reserver: 'Réserver ma place',
    reserveSucces: 'Réservation confirmée.',
    reservation: 'Réservation en cours…',
    procederPaiement: '💳 Procéder au paiement',
    complet: 'Complet — toutes les places sont prises',

    // Propriétaire
    sectionProprio: 'Propriétaire',
    aucuneEvaluation: "Aucune évaluation pour l'instant",
    contacter: 'Contacter le propriétaire',
    contactDev: 'Messagerie available prochainement.',

    // Statuts
    statutBrouillon: 'Brouillon',
    statutEnCours: 'En cours',
    statutTerminee: 'Terminé',
    statutAnnulee: 'Annulé',
    statutAdjugee: 'Adjugé',
  },
  en: {
    chargement: 'Loading…',
    erreur: 'Could not load this auction.',
    introuvable: 'Auction not found.',
    retour: '← Back to listings',
    photoSeule: 'No photo available',

    sectionChasse: 'Hunting information',
    especesAutorisees: 'Allowed species',
    armesAutorisees: 'Allowed weapons',
    saisonChasse: 'Hunting season',
    superficieCh: 'Huntable area',
    zone: 'Hunting zone',
    capacite: 'Capacity',
    placesDispo: (d, t) => `${d} place${d > 1 ? 's' : ''} of ${t} available`,
    reglements: 'Applicable regulations',
    voirReglements: 'Show regulations',
    masquerReglements: 'Hide regulations',
    aucunReglement: 'No regulations available.',
    hebergement: 'Lodging included',
    guide: 'Guide included',
    notes: 'Owner notes',

    sectionEncan: 'Live auction',
    prixActuel: 'Current price',
    prixPlancher: 'Floor price',
    aucuneMise: 'No bids yet',
    historiqueMises: 'Recent bids',
    placerMise: 'Place a bid',
    miseMin: (m) => `Minimum bid: $${m.toFixed(2)}`,
    montantInvalide: `Bid must be at least $${PAS_MISE} above current.`,
    placer: 'Place my bid',
    placement: 'Placing…',
    miseSucces: 'Bid placed successfully!',
    connexionRequise: 'Sign in to place a bid.',
    chasseurUniquement: 'Hunters only.',

    fermetureDans: 'Closes in',
    encanTermine: 'Auction ended',
    jours: 'd',
    heures: 'h',
    minutes: 'm',
    secondes: 's',

    sectionPrixFixe: 'Fixed-price booking',
    prixParChasseur: 'Price per hunter',
    placesRestantes: (r, t) => `${r} place${r > 1 ? 's' : ''} of ${t} remaining`,
    reserver: 'Reserve my place',
    reserveSucces: 'Reservation confirmed.',
    reservation: 'Reserving…',
    procederPaiement: '💳 Proceed to payment',
    complet: 'Sold out — all places taken',

    sectionProprio: 'Owner',
    aucuneEvaluation: 'No reviews yet',
    contacter: 'Contact owner',
    contactDev: 'Messaging available soon.',

    statutBrouillon: 'Draft',
    statutEnCours: 'Live',
    statutTerminee: 'Ended',
    statutAnnulee: 'Cancelled',
    statutAdjugee: 'Awarded',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function extraireLatLng(terre) {
  if (!terre) return null;
  const loc = terre.localisation;
  if (loc && Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
    return { lat: loc.coordinates[1], lng: loc.coordinates[0] };
  }
  if (terre.latitude != null && terre.longitude != null) {
    return { lat: Number(terre.latitude), lng: Number(terre.longitude) };
  }
  return null;
}

function prenomSeul(name) {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/);
  return parts[0] || '';
}

function calculerCompteARebours(dateFermeture) {
  if (!dateFermeture) return null;
  const reste = new Date(dateFermeture).getTime() - Date.now();
  if (isNaN(reste)) return null;
  if (reste <= 0) return { termine: true, j: 0, h: 0, m: 0, s: 0 };
  const j = Math.floor(reste / 86_400_000);
  const h = Math.floor((reste / 3_600_000) % 24);
  const m = Math.floor((reste / 60_000) % 60);
  const s = Math.floor((reste / 1000) % 60);
  return { termine: false, j, h, m, s };
}

function formatMontant(v, lang) {
  if (v == null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatHeure(date, lang) {
  if (!date) return '';
  try {
    return new Date(date).toLocaleTimeString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function STATUT_LABEL(t, status) {
  return (
    {
      brouillon: t.statutBrouillon,
      en_cours: t.statutEnCours,
      terminee: t.statutTerminee,
      annulee: t.statutAnnulee,
      adjugee: t.statutAdjugee,
    }[status] || status
  );
}

// ── Marker pour la mini-carte ─────────────────────────────────────────────────

const ICON_TERRE = createDivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="38" viewBox="0 0 22 32">
    <path d="M11 0C4.9 0 0 4.9 0 11c0 8.3 11 21 11 21s11-12.7 11-21C22 4.9 17.1 0 11 0z"
      fill="${VERT}" stroke="${OR}" stroke-width="1.6"/>
    <circle cx="11" cy="11" r="4.5" fill="${OR}" opacity="0.95"/>
  </svg>`,
});

// ── Sous-composants ───────────────────────────────────────────────────────────

function Carousel({ photos, t }) {
  const [index, setIndex] = useState(0);

  if (!photos || photos.length === 0) {
    return (
      <div
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${BORDURE}`,
          borderRadius: '8px',
          height: 280,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TEXTE_DIM,
          fontSize: '0.9rem',
        }}
      >
        🏞 {t.photoSeule}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#000',
        height: 360,
      }}
    >
      <img
        src={photos[index]}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + photos.length) % photos.length)}
            aria-label="←"
            style={{
              position: 'absolute',
              top: '50%',
              left: 10,
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              borderRadius: '50%',
              width: 36,
              height: 36,
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % photos.length)}
            aria-label="→"
            style={{
              position: 'absolute',
              top: '50%',
              right: 10,
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              borderRadius: '50%',
              width: 36,
              height: 36,
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            ›
          </button>
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 6,
            }}
          >
            {photos.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: i === index ? OR : 'rgba(255,255,255,0.4)',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CompteARebours({ fermeture, t }) {
  const [reste, setReste] = useState(() => calculerCompteARebours(fermeture));

  useEffect(() => {
    if (!fermeture) return;
    const id = setInterval(() => setReste(calculerCompteARebours(fermeture)), 1000);
    return () => clearInterval(id);
  }, [fermeture]);

  if (!reste) return null;

  if (reste.termine) {
    return (
      <div
        style={{
          background: 'rgba(200,50,50,0.14)',
          border: '1px solid rgba(200,50,50,0.5)',
          borderRadius: '6px',
          padding: '0.7rem 1rem',
          color: 'var(--couleur-texte-erreur)',
          fontSize: '0.9rem',
          fontWeight: 700,
          textAlign: 'center',
        }}
      >
        ⏱ {t.encanTermine}
      </div>
    );
  }

  const segs = [
    { val: reste.j, lbl: t.jours },
    { val: reste.h, lbl: t.heures },
    { val: reste.m, lbl: t.minutes },
    { val: reste.s, lbl: t.secondes },
  ];

  return (
    <div>
      <div
        style={{
          fontSize: '0.74rem',
          color: TEXTE_DIM,
          textAlign: 'center',
          marginBottom: '0.5rem',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        {t.fermetureDans}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
        {segs.map((s, i) => (
          <div
            key={i}
            style={{
              background: 'rgba(184,134,11,0.12)',
              border: `1px solid ${OR}66`,
              borderRadius: '6px',
              padding: '0.55rem 0.8rem',
              minWidth: '54px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '1.4rem',
                fontWeight: 800,
                color: OR,
                lineHeight: 1,
                fontFamily: 'monospace',
              }}
            >
              {String(s.val).padStart(2, '0')}
            </div>
            <div
              style={{
                fontSize: '0.66rem',
                color: TEXTE_DIM,
                marginTop: '0.25rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {s.lbl}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Badge({ children, couleur = OR }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '3px 9px',
        borderRadius: '20px',
        fontSize: '0.74rem',
        fontWeight: 600,
        background: `${couleur}22`,
        border: `1px solid ${couleur}77`,
        color: couleur === OR ? OR : 'var(--couleur-texte)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function MiniCarte({ terre }) {
  const coord = extraireLatLng(terre);
  if (!coord) return null;

  return (
    <div
      style={{
        height: 240,
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${BORDURE}`,
      }}
    >
      {/* Mini-carte satellite Esri (couvre toutes les régions QC + Canada)
          recentrée par flyToCenter dès que `coord` change. scrollWheelZoom
          off : c'est une vue compagnon, pas une carte explorable. */}
      <ChasseHuntMap
        center={[coord.lat, coord.lng]}
        zoom={11}
        basemap="satellite"
        height="100%"
        scrollWheelZoom={false}
        flyToCenter
        markers={[{ id: terre.id, lat: coord.lat, lng: coord.lng, icon: ICON_TERRE }]}
      />
    </div>
  );
}

function HistoriqueMises({ bids, lang, t }) {
  if (!bids || bids.length === 0) {
    return (
      <div style={{ color: TEXTE_DIM, fontSize: '0.85rem', padding: '0.5rem 0' }}>
        {t.aucuneMise}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: '0.4rem' }}>
      {bids.slice(0, 8).map((m) => (
        <div
          key={m.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: m.status === 'active' ? `${OR}15` : 'rgba(255,255,255,0.03)',
            border: `1px solid ${m.status === 'active' ? OR + '55' : BORDURE}`,
            borderRadius: '5px',
            padding: '0.4rem 0.75rem',
            fontSize: '0.82rem',
          }}
        >
          <span style={{ color: TEXTE_DIM, fontSize: '0.78rem' }}>
            {m.chasseur_anonymise || 'Chasseur'}
          </span>
          <span
            style={{
              color: m.status === 'active' ? OR : TEXTE_DIM,
              fontWeight: m.status === 'active' ? 700 : 500,
            }}
          >
            {formatMontant(m.amount, lang)}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>
            {formatHeure(m.createdAt, lang)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

export default function DetailEncan() {
  const { id } = useParams();
  const { lang } = useContext(LangContext);
  const { utilisateur, estConnecte } = useAuth();
  const navigate = useNavigate();
  const t = T[lang];

  const [pub, setPub] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const [reglementsOuverts, setReglementsOuverts] = useState(false);

  // Mise / réservation
  const [montantMise, setMontantMise] = useState('');
  const [actionEnCours, setActionEnCours] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionErreur, setActionErreur] = useState('');
  // bid_id retourné par /listings/:id/reserver pour rediriger vers /paiement/:miseId
  const [miseIdPaiement, setMiseIdPaiement] = useState(null);

  const utilisateurEstChasseur = utilisateur?.user_type === 'hunter';

  // ── Chargement initial + polling ────────────────────────────────────────

  const charger = useRef(null);
  charger.current = async (silencieux = false) => {
    if (!silencieux) setChargement(true);
    try {
      const res = await api.get(`/listings/${id}`);
      setPub(res.data);
      setErreur('');
    } catch (err) {
      if (err.response?.status === 404) setErreur('introuvable');
      else setErreur('erreur');
    } finally {
      if (!silencieux) setChargement(false);
    }
  };

  useEffect(() => {
    charger.current(false);
    const intervalId = setInterval(() => charger.current(true), POLLING_MS);
    return () => clearInterval(intervalId);
  }, [id]);

  // ── Live updates via Server-Sent Events ─────────────────────────────────
  //
  // À chaque bid_placed (ou auction_closed) on refetch le détail complet
  // plutôt que d'appliquer un diff côté client : l'API retourne déjà le
  // tableau bids reconstruit avec les status (perdue/active) et le
  // montant_actuel calculé serveur — répliquer cette logique en JS
  // multiplierait les sources de vérité. Le refetch est cheap (~1 KB).
  // api.defaults.baseURL n'existe pas quand axios est stubbé (tests).
  // Fallback sur le chemin relatif — le hook gère un sseUrl falsy aussi.
  const apiBase = api?.defaults?.baseURL || '/api/v1';
  const sseUrl = id ? `${apiBase}/listings/${id}/events` : null;

  const onSseEvent = useCallback(
    (evt) => {
      if (evt.type === 'bid_placed' || evt.type === 'auction_closed') {
        charger.current?.(true);
      }
    },
    // charger est dans une ref ; pas besoin de la lister
    []
  );
  useEventSource(sseUrl, { onEvent: onSseEvent, enabled: !!id });

  // ── Mise minimum dérivée ────────────────────────────────────────────────

  const miseMinimum = useMemo(() => {
    if (!pub || pub.mode !== 'enchere') return 0;
    const courant =
      pub.montant_actuel != null ? Number(pub.montant_actuel) : Number(pub.floor_price) || 0;
    return courant + PAS_MISE;
  }, [pub]);

  const placerMise = async () => {
    if (!estConnecte) {
      navigate('/login');
      return;
    }
    const amount = Number(montantMise);
    if (isNaN(amount) || amount < miseMinimum) {
      setActionErreur(t.montantInvalide);
      return;
    }

    setActionEnCours(true);
    setActionMessage('');
    setActionErreur('');
    try {
      await api.post(`/listings/${id}/mise`, { amount });
      setActionMessage(t.miseSucces);
      setMontantMise('');
      await charger.current(true);
    } catch (err) {
      setActionErreur(err.response?.data?.error || t.erreur);
    } finally {
      setActionEnCours(false);
    }
  };

  const reserver = async () => {
    if (!estConnecte) {
      navigate('/login');
      return;
    }
    setActionEnCours(true);
    setActionMessage('');
    setActionErreur('');
    try {
      const res = await api.post(`/listings/${id}/reserver`);
      setActionMessage(res.data?.message || t.reserveSucces);
      if (res.data?.reservation?.id) {
        setMiseIdPaiement(res.data.reservation.id);
      }
      await charger.current(true);
    } catch (err) {
      setActionErreur(err.response?.data?.error || t.erreur);
    } finally {
      setActionEnCours(false);
    }
  };

  // ── Render — états de chargement/erreur ─────────────────────────────────

  if (chargement) {
    return (
      <PageShell>
        <div style={{ textAlign: 'center', padding: '4rem', color: TEXTE_DIM }}>{t.chargement}</div>
      </PageShell>
    );
  }

  if (erreur === 'introuvable') {
    return (
      <PageShell>
        <div style={{ textAlign: 'center', padding: '4rem', color: TEXTE_DIM }}>
          🌲 {t.introuvable}
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/lands" style={{ color: OR, fontSize: '0.9rem' }}>
              {t.retour}
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  if (erreur || !pub) {
    return (
      <PageShell>
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--couleur-texte-erreur)' }}>
          ⚠ {t.erreur}
        </div>
      </PageShell>
    );
  }

  // ── Données dérivées ────────────────────────────────────────────────────

  const terre = pub.terre || {};
  const proprio = pub.landowner || terre.landowner || {};
  const photos = terre.photos || [];
  const especes = pub.targeted_species || [];
  const armes = pub.weapon_types || [];
  const reglements = pub.reglements || [];

  const capacite = pub.max_seats || terre.max_hunters || 1;
  const placesRestantes =
    pub.places_restantes != null
      ? pub.places_restantes
      : Math.max(0, capacite - (pub.nb_reservations || 0));

  const estEnCours = pub.status === 'en_cours';
  const compteARebours = pub.auction_closes_at || pub.end_date;

  // ── Render principal ───────────────────────────────────────────────────

  return (
    <PageShell>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '2rem 1.5rem 5rem' }}>
        <Link
          to="/lands"
          style={{
            display: 'inline-block',
            fontSize: '0.78rem',
            color: TEXTE_DIM,
            textDecoration: 'none',
            marginBottom: '1.2rem',
          }}
        >
          {t.retour}
        </Link>

        <div
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: '1.6rem' }}
        >
          {/* ─── Colonne principale ─── */}
          <div style={{ display: 'grid', gap: '1.5rem', minWidth: 0 }}>
            {/* Titre + status */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  marginBottom: '0.3rem',
                  flexWrap: 'wrap',
                }}
              >
                <Badge couleur={estEnCours ? OR : TEXTE_DIM}>{STATUT_LABEL(t, pub.status)}</Badge>
                <span style={{ fontSize: '0.78rem', color: TEXTE_DIM }}>{terre.province}</span>
              </div>
              <h1
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.7rem',
                  color: 'var(--couleur-texte)',
                  margin: 0,
                  lineHeight: 1.25,
                }}
              >
                {terre.title || '—'}
              </h1>
            </div>

            {/* Section 1 — Photos + carte */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.9rem' }}>
              <Carousel photos={photos} t={t} />
              <MiniCarte terre={terre} />
            </div>

            {/* Section 2 — Infos chasse */}
            <div
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${BORDURE}`,
                borderRadius: '10px',
                padding: '1.2rem 1.4rem',
              }}
            >
              <h2
                style={{
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: OR,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 1rem',
                }}
              >
                {t.sectionChasse}
              </h2>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ ...subTitre }}>{t.especesAutorisees}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {especes.length === 0 && (
                    <span style={{ color: TEXTE_DIM, fontSize: '0.85rem' }}>—</span>
                  )}
                  {especes.map((v) => {
                    const e = ESPECES[v];
                    return <Badge key={v}>{e ? `${e.icon} ${e[lang]}` : v}</Badge>;
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={subTitre}>{t.armesAutorisees}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {armes.length === 0 && (
                    <span style={{ color: TEXTE_DIM, fontSize: '0.85rem' }}>—</span>
                  )}
                  {armes.map((v) => {
                    const a = ARMES[v];
                    return (
                      <Badge key={v} couleur={TEXTE_DIM}>
                        {a ? `${a.icon} ${a[lang]}` : v}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '0.8rem 1.2rem',
                  marginBottom: '0.5rem',
                }}
              >
                <Donnee
                  label={t.saisonChasse}
                  value={
                    pub.hunt_start_date && pub.hunt_end_date
                      ? `${pub.hunt_start_date} → ${pub.hunt_end_date}`
                      : '—'
                  }
                />
                <Donnee
                  label={t.superficieCh}
                  value={pub.huntable_area_ha ? `${pub.huntable_area_ha} ha` : '—'}
                />
                <Donnee label={t.zone} value={pub.hunting_zone_id || '—'} />
                <Donnee
                  label={t.capacite}
                  value={estEnCours ? t.placesDispo(placesRestantes, capacite) : `${capacite}`}
                />
              </div>

              {(pub.lodging_included || pub.guide_included) && (
                <div
                  style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.7rem' }}
                >
                  {pub.lodging_included && <Badge>🏕 {t.hebergement}</Badge>}
                  {pub.guide_included && <Badge>⛺ {t.guide}</Badge>}
                </div>
              )}

              {pub.specific_notes && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={subTitre}>{t.notes}</div>
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: `1px solid ${BORDURE}`,
                      borderRadius: '6px',
                      padding: '0.7rem 0.9rem',
                      fontSize: '0.85rem',
                      color: TEXTE,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {pub.specific_notes}
                  </div>
                </div>
              )}

              {/* Règlements expandable */}
              <div
                style={{
                  marginTop: '1.1rem',
                  paddingTop: '1rem',
                  borderTop: `1px solid ${BORDURE}`,
                }}
              >
                <button
                  type="button"
                  onClick={() => setReglementsOuverts((v) => !v)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: OR,
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  📜 {reglementsOuverts ? t.masquerReglements : t.voirReglements} (
                  {reglements.length})
                </button>
                {reglementsOuverts && (
                  <div style={{ marginTop: '0.7rem', display: 'grid', gap: '0.5rem' }}>
                    {reglements.length === 0 ? (
                      <div style={{ color: TEXTE_DIM, fontSize: '0.84rem' }}>
                        {t.aucunReglement}
                      </div>
                    ) : (
                      reglements.map((r, i) => (
                        <div
                          key={r.id || i}
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: `1px solid ${BORDURE}`,
                            borderRadius: '6px',
                            padding: '0.55rem 0.8rem',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: '0.83rem',
                              color: 'var(--couleur-texte)',
                            }}
                          >
                            {lang === 'fr' ? r.species_fr : r.species_en || r.species_fr}
                          </div>
                          <div style={{ fontSize: '0.74rem', color: TEXTE_DIM, marginTop: '2px' }}>
                            {r.weapon_type && <span>{r.weapon_type} · </span>}
                            {r.season_type && <span>{r.season_type} · </span>}
                            {r.start_date && r.end_date && (
                              <span>
                                {r.start_date} → {r.end_date}
                              </span>
                            )}
                          </div>
                          {r.daily_limit && (
                            <div style={{ fontSize: '0.72rem', color: OR, marginTop: '3px' }}>
                              Limite/jour : {r.daily_limit}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Colonne latérale (sticky) ─── */}
          <aside style={{ display: 'grid', gap: '1rem', alignContent: 'start' }}>
            {/* Section 3 — Encan ou Prix fixe */}
            {pub.mode === 'enchere' ? (
              <div
                style={{
                  background: `${VERT}55`,
                  border: `1px solid ${OR}66`,
                  borderRadius: '10px',
                  padding: '1.2rem 1.3rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: OR,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    margin: '0 0 0.8rem',
                    textAlign: 'center',
                  }}
                >
                  🏷 {t.sectionEncan}
                </h2>

                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.74rem', color: TEXTE_DIM, marginBottom: '0.2rem' }}>
                    {pub.montant_actuel ? t.prixActuel : t.prixPlancher}
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: 800, color: OR, lineHeight: 1.1 }}>
                    {formatMontant(pub.montant_actuel ?? pub.floor_price, lang)}
                  </div>
                </div>

                {estEnCours && (
                  <div style={{ marginBottom: '1.1rem' }}>
                    <CompteARebours fermeture={compteARebours} t={t} />
                  </div>
                )}

                {/* Champ mise + bouton — chasseur seulement */}
                {estEnCours && (
                  <div style={{ marginTop: '0.8rem' }}>
                    {!estConnecte && (
                      <Link
                        to="/login"
                        style={{
                          display: 'block',
                          textAlign: 'center',
                          background: OR,
                          color: 'var(--couleur-fond-carte)',
                          padding: '0.7rem',
                          borderRadius: '6px',
                          fontSize: '0.88rem',
                          fontWeight: 700,
                          textDecoration: 'none',
                        }}
                      >
                        🔐 {t.connexionRequise}
                      </Link>
                    )}
                    {estConnecte && !utilisateurEstChasseur && (
                      <div
                        style={{
                          textAlign: 'center',
                          background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${BORDURE}`,
                          borderRadius: '6px',
                          padding: '0.6rem',
                          color: TEXTE_DIM,
                          fontSize: '0.82rem',
                        }}
                      >
                        {t.chasseurUniquement}
                      </div>
                    )}
                    {estConnecte && utilisateurEstChasseur && (
                      <>
                        <div
                          style={{
                            fontSize: '0.74rem',
                            color: TEXTE_DIM,
                            marginBottom: '0.4rem',
                            textAlign: 'center',
                          }}
                        >
                          {t.miseMin(miseMinimum)}
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <input
                            type="number"
                            min={miseMinimum}
                            step={1}
                            value={montantMise}
                            onChange={(e) => setMontantMise(e.target.value)}
                            placeholder={String(miseMinimum)}
                            style={{
                              flex: 1,
                              background: 'rgba(0,0,0,0.4)',
                              border: `1px solid ${BORDURE}`,
                              borderRadius: '5px',
                              color: TEXTE,
                              padding: '0.55rem 0.7rem',
                              fontSize: '0.95rem',
                              fontFamily: 'monospace',
                              outline: 'none',
                            }}
                          />
                          <button
                            type="button"
                            onClick={placerMise}
                            disabled={actionEnCours}
                            style={{
                              background: OR,
                              color: 'var(--couleur-fond-carte)',
                              border: 'none',
                              borderRadius: '5px',
                              padding: '0.55rem 1.1rem',
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              cursor: actionEnCours ? 'not-allowed' : 'pointer',
                              opacity: actionEnCours ? 0.6 : 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {actionEnCours ? '…' : t.placer}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {actionMessage && (
                  <div
                    style={{
                      marginTop: '0.7rem',
                      background: 'rgba(40,160,80,0.12)',
                      border: '1px solid rgba(40,160,80,0.4)',
                      borderRadius: '5px',
                      padding: '0.5rem 0.7rem',
                      color: 'var(--couleur-texte-succes)',
                      fontSize: '0.78rem',
                      textAlign: 'center',
                    }}
                  >
                    {actionMessage}
                  </div>
                )}
                {actionErreur && (
                  <div
                    style={{
                      marginTop: '0.7rem',
                      background: 'rgba(200,50,50,0.12)',
                      border: '1px solid rgba(200,50,50,0.4)',
                      borderRadius: '5px',
                      padding: '0.5rem 0.7rem',
                      color: 'var(--couleur-texte-erreur)',
                      fontSize: '0.78rem',
                      textAlign: 'center',
                    }}
                  >
                    {actionErreur}
                  </div>
                )}

                {/* Historique */}
                <div
                  style={{
                    marginTop: '1.1rem',
                    paddingTop: '0.9rem',
                    borderTop: `1px solid ${BORDURE}`,
                  }}
                >
                  <div style={{ ...subTitre, marginBottom: '0.5rem' }}>{t.historiqueMises}</div>
                  <HistoriqueMises bids={pub.bids} lang={lang} t={t} />
                </div>
              </div>
            ) : (
              <div
                style={{
                  background: `${VERT}55`,
                  border: `1px solid ${OR}66`,
                  borderRadius: '10px',
                  padding: '1.2rem 1.3rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: OR,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    margin: '0 0 0.8rem',
                    textAlign: 'center',
                  }}
                >
                  📋 {t.sectionPrixFixe}
                </h2>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.74rem', color: TEXTE_DIM, marginBottom: '0.2rem' }}>
                    {t.prixParChasseur}
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: 800, color: OR, lineHeight: 1.1 }}>
                    {formatMontant(pub.fixed_price, lang)}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: TEXTE, marginTop: '0.6rem' }}>
                    {t.placesRestantes(placesRestantes, capacite)}
                  </div>
                </div>

                {estEnCours && compteARebours && (
                  <div style={{ marginBottom: '1.1rem' }}>
                    <CompteARebours fermeture={compteARebours} t={t} />
                  </div>
                )}

                {placesRestantes <= 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      background: 'rgba(200,50,50,0.12)',
                      border: '1px solid rgba(200,50,50,0.4)',
                      borderRadius: '5px',
                      padding: '0.7rem',
                      color: 'var(--couleur-texte-erreur)',
                      fontSize: '0.84rem',
                      fontWeight: 700,
                    }}
                  >
                    {t.complet}
                  </div>
                ) : !estConnecte ? (
                  <Link
                    to="/login"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      background: OR,
                      color: 'var(--couleur-fond-carte)',
                      padding: '0.7rem',
                      borderRadius: '6px',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    🔐 {t.connexionRequise}
                  </Link>
                ) : !utilisateurEstChasseur ? (
                  <div
                    style={{
                      textAlign: 'center',
                      background: 'rgba(255,255,255,0.04)',
                      border: `1px solid ${BORDURE}`,
                      borderRadius: '6px',
                      padding: '0.6rem',
                      color: TEXTE_DIM,
                      fontSize: '0.82rem',
                    }}
                  >
                    {t.chasseurUniquement}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={reserver}
                    disabled={actionEnCours}
                    style={{
                      width: '100%',
                      background: OR,
                      color: 'var(--couleur-fond-carte)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.7rem',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      cursor: actionEnCours ? 'not-allowed' : 'pointer',
                      opacity: actionEnCours ? 0.6 : 1,
                    }}
                  >
                    {actionEnCours ? t.reservation : `🎟 ${t.reserver}`}
                  </button>
                )}

                {actionMessage && (
                  <div
                    style={{
                      marginTop: '0.7rem',
                      background: 'rgba(40,160,80,0.12)',
                      border: '1px solid rgba(40,160,80,0.4)',
                      borderRadius: '5px',
                      padding: '0.5rem 0.7rem',
                      color: 'var(--couleur-texte-succes)',
                      fontSize: '0.78rem',
                      textAlign: 'center',
                    }}
                  >
                    {actionMessage}
                  </div>
                )}
                {actionErreur && (
                  <div
                    style={{
                      marginTop: '0.7rem',
                      background: 'rgba(200,50,50,0.12)',
                      border: '1px solid rgba(200,50,50,0.4)',
                      borderRadius: '5px',
                      padding: '0.5rem 0.7rem',
                      color: 'var(--couleur-texte-erreur)',
                      fontSize: '0.78rem',
                      textAlign: 'center',
                    }}
                  >
                    {actionErreur}
                  </div>
                )}

                {miseIdPaiement && (
                  <Link
                    to={`/paiement/${miseIdPaiement}`}
                    style={{
                      display: 'block',
                      marginTop: '0.7rem',
                      background: OR,
                      color: 'var(--couleur-fond-carte)',
                      borderRadius: '6px',
                      padding: '0.7rem',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    {t.procederPaiement}
                  </Link>
                )}
              </div>
            )}

            {/* Section 4 — Propriétaire */}
            <div
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${BORDURE}`,
                borderRadius: '10px',
                padding: '1.1rem 1.3rem',
              }}
            >
              <h2
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: OR,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 0.7rem',
                }}
              >
                {t.sectionProprio}
              </h2>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                  marginBottom: '0.7rem',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: `${VERT}88`,
                    border: `1px solid ${OR}66`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: OR,
                    flexShrink: 0,
                  }}
                >
                  {prenomSeul(proprio.name).charAt(0).toUpperCase() || '🏡'}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: 'var(--couleur-texte)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {prenomSeul(proprio.name) || '—'}
                  </div>
                  {/* Étoiles d'évaluation (placeholder, système non encore implémenté) */}
                  <div
                    style={{
                      fontSize: '0.78rem',
                      color: TEXTE_DIM,
                      marginTop: '2px',
                    }}
                  >
                    <span style={{ color: 'rgba(184,134,11,0.4)', letterSpacing: '0.1em' }}>
                      ☆☆☆☆☆
                    </span>
                    <span style={{ marginLeft: '0.4rem', fontSize: '0.72rem' }}>
                      {t.aucuneEvaluation}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => alert(t.contactDev)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: `1px solid ${OR}66`,
                  color: OR,
                  borderRadius: '6px',
                  padding: '0.55rem',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                ✉ {t.contacter}
              </button>
            </div>
          </aside>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          [data-detail-auction-grid] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </PageShell>
  );
}

// ── Styles utilitaires (déclarés en bas pour lisibilité) ─────────────────────

const subTitre = {
  fontSize: '0.7rem',
  color: TEXTE_DIM,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '0.4rem',
};

function Donnee({ label, value }) {
  return (
    <div>
      <div style={subTitre}>{label}</div>
      <div style={{ fontSize: '0.88rem', color: 'var(--couleur-texte)', fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}
